import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";

import { useLargeReportConfirm } from "src/hooks/useLargeReportConfirm";
import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";

import { ActionButton, CardLayout, SelectField } from "../v3";
import { ProjectService, PurchaseService } from "../../services";
import { materialReportValidation } from "../../pages/report/validation";
import { getFilteredOptions, selectAllOption } from "../../utils";
import { ReportDateRangeFields } from "./ReportDateRangeFields.component";

export const MaterialReportForm = ({ reportType = "summary", handleClear = () => {}, onGenerate = () => {} }) => {
  const { months: maxRangeMonths } = useReportRangeLimit();
  const { confirmLargeRange, largeRangeDialog } = useLargeReportConfirm();
  const [selectedMaterials, setSelectedMaterials] = useState([selectAllOption]);
  const [selectedVendors, setSelectedVendors] = useState([selectAllOption]);
  const [selectedProjects, setSelectedProjects] = useState([selectAllOption]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    materialIds: null,
    vendorIds: null,
    projectIds: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  const updateDetailsState = (event) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectionChange = (selected, setSelections, reportKey, errorKey) => {
    setLoading(false);
    handleClear();
    const allSelected = selected.some((item) => item.id === "all");

    if (allSelected) {
      setSelections([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, [reportKey]: null }));
    } else {
      const filtered = selected.filter((item) => item.id !== "all");
      setSelections(filtered);
      setReportDetails((prev) => ({ ...prev, [reportKey]: filtered.map((item) => item.id) }));
    }

    setErrors((prev) => ({ ...prev, [errorKey]: "" }));
  };

  const handleGenerateReport = async () => {
    setErrors({});
    const errors = materialReportValidation(
      reportDetails,
      selectedMaterials[0]?.id,
      selectedVendors[0]?.id,
      selectedProjects[0]?.id,
      maxRangeMonths,
    );
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (!(await confirmLargeRange(reportDetails.fromDate, reportDetails.toDate))) return;

    try {
      setLoading(true);
      // Convert dates to ISO 8601 format
      const isoFromDate = moment(reportDetails.fromDate).startOf("day").toISOString();
      const isoToDate = moment(reportDetails.toDate).endOf("day").toISOString();

      const payload = {
        ...reportDetails,
        fromDate: isoFromDate,
        toDate: isoToDate,
        reportType,
      };

      onGenerate?.(payload, {
        onError: () => setLoading(false),
      });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <CardLayout>
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <SelectField
            label="Material"
            value={selectedMaterials}
            getData={PurchaseService.getPurchaseItems}
            getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
            required
            multiSelect
            error={!!errors.materialIds}
            helperText={errors.materialIds}
            onChange={(selected) => handleSelectionChange(selected, setSelectedMaterials, "materialIds", "materialIds")}
            optionFilter={(options) => getFilteredOptions(options, selectedMaterials, selectAllOption)}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            label="Vendor"
            value={selectedVendors}
            getData={PurchaseService.getVendors}
            getDataStaticFilters={{ type: "VENDOR", sortBy: "name", sortOrder: "asc" }}
            required
            multiSelect
            error={!!errors.vendorIds}
            helperText={errors.vendorIds}
            onChange={(selected) => handleSelectionChange(selected, setSelectedVendors, "vendorIds", "vendorIds")}
            optionFilter={(options) => getFilteredOptions(options, selectedVendors, selectAllOption)}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Project"
            value={selectedProjects}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
            required
            multiSelect
            error={!!errors.projectIds}
            helperText={errors.projectIds}
            onChange={(selected) => handleSelectionChange(selected, setSelectedProjects, "projectIds", "projectIds")}
            optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
            style={{ width: "100%" }}
          />
        </Box>
        <ReportDateRangeFields
          fromDate={reportDetails.fromDate}
          toDate={reportDetails.toDate}
          onChange={updateDetailsState}
          errors={errors}
        />
        {largeRangeDialog}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
      </Box>
    </CardLayout>
  );
};
